// 🔴 « Visite Rénovation » — ENTRÉE V1 (P-MARIO-03) de l'application terrain.
//
// 🔑 MODULE D'ENTRÉE, hors de `pages/chantier/` : il ORCHESTRE des routes d'autres
//    domaines (clients, lieux, dossiers) ; le relevé (`pages/chantier/`), lui, reste
//    strictement sur `/api/chantier/` (garde_frontiere_artisan).
//
//   utilisateur OmniHub AUTHENTIFIÉ → « Nouvelle visite » | « Reprendre une visite »
//   Nouvelle visite : client + adresse (dictés ou saisis) → clients proposés → choix
//   → lieux proposés → choix (ou nouveau lieu) → dossier (existant ou nouveau)
//   → CONFIRMATION explicite → dossier/lieu créés si besoin → relevé ouvert.
//
// ⛔ AUCUN SMS, ⛔ aucun lien à coller, ⛔ aucun code, ⛔ aucun numéro de dossier demandé.
// ⛔ AUCUNE écriture avant « Confirmer » : tout ce qui précède est LECTURE.
// 🔑 Capacités DÉCLARÉES par le tenant (⛔ aucun nom de tenant) : `profil_chantier`
//    (quel relevé ouvrir) et `dossier_activite` (quel dossier créer).
// 🔑 Reprise : relevés non clos de L'UTILISATEUR de la session (serveur), ⛔ un stockage local.
// ⚠️ Nom produit INVARIANT : « Visite Rénovation ».
import { useCallback, useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import { Encart } from "../chantier/AnnexesReleve";
import { ReleveChantier } from "../chantier/ReleveChantier";
import { CreerDevisReleve } from "../metier/artisan/CreerDevisReleve";
import { pdfDevis } from "../metier/artisan/api";
import { useDictee } from "../chantier/useDictee";
import { capacitesDeLaSession, chercherClients, confirmer, decouperDictee, lieuxDuClient, mesVisitesEnCours } from "./entreeVisite";
import { getDossierActivite, profilChantier } from "../../utils/tenantProfile";
import { useAuth } from "../../contexts/AuthContext";
import ConnexionTerrain from "../../components/auth/ConnexionTerrain";

const NOM = "Visite Rénovation";

/** 🔴 N°975 — sur le terrain, « Ouvrir le devis » montre SON PDF (route documentaire
 *  partagée, lue avec la session) : ⛔ pas de navigation vers l'ERP depuis le téléphone. */
async function ouvrirPdfDevis(devis) {
  const url = URL.createObjectURL(await pdfDevis(devis.devis_id));
  if (!window.open(url, "_blank")) window.location.assign(url);
}
const MANIFEST = "/manifest-renovation.json";

const CARTE = { background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)", borderRadius: 12,
                padding: 16, display: "grid", gap: 8, color: "var(--oh-text)" };
const CHAMP = { height: 44, borderRadius: 8, border: "0.5px solid var(--oh-border)", padding: "0 12px",
                background: "var(--oh-bg)", color: "var(--oh-text)", fontSize: 16 };
const PRIMAIRE = { background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)", height: 44 };
const SECONDAIRE = { border: "0.5px solid var(--oh-accent)", color: "var(--oh-accent-text)", background: "transparent", height: 44 };
const DISCRET = { border: "0.5px solid var(--oh-border)", color: "var(--oh-text-secondary)", background: "transparent", height: 36 };

const adresseLieu = (s) => [s.nom_site, s.adresse, [s.code_postal, s.ville].filter(Boolean).join(" ")].filter(Boolean).join(" · ");
// ⚠️ `display_name` de la recherche = « société (prénom nom) » ⇒ « ABC (ABC) » : la raison
//    sociale d'abord pour une personne morale.
const nomClient = (c) => c.raison_sociale || c.display_name || [c.prenom, c.nom].filter(Boolean).join(" ");


/** Le manifest de CETTE application, le temps de l'écran : installée, elle rouvre ICI. */
function useManifest() {
  useEffect(() => {
    const lien = document.querySelector('link[rel="manifest"]');
    if (!lien) return undefined;
    const precedent = lien.getAttribute("href");
    lien.setAttribute("href", MANIFEST);
    return () => { if (precedent) lien.setAttribute("href", precedent); };
  }, []);
}

/** Capacités DÉCLARÉES (profil de relevé, activité du dossier). Cache d'OmniHub d'abord ;
 *  vide ⇒ lecture serveur — ⛔ afficher « aucun profil » sur un simple cache vide serait faux. */
//  🔑 Relu À CHAQUE ouverture de session : sur `visite.*`, OmniHub ⛔ tourne jamais et la
//     connexion ⛔ pose pas ces clés — sans relecture, l'écran dirait « aucun profil ».
function useCapacites(session) {
  const [etat, setEtat] = useState({ pret: false, profil: null, activite: null });
  useEffect(() => {
    if (!session) { setEtat({ pret: false, profil: null, activite: null }); return undefined; }
    const profil = profilChantier(), activite = getDossierActivite();
    if (profil && activite) { setEtat({ pret: true, profil, activite }); return undefined; }
    let vivant = true;
    capacitesDeLaSession()
      .then((c) => vivant && setEtat({ pret: true, ...c }))
      .catch(() => vivant && setEtat({ pret: true, profil: null, activite: null }));
    return () => { vivant = false; };
  }, [session]);
  return etat;
}

function Champ({ libelle, valeur, onChange, testId, placeholder }) {
  return (
    <label className="text-sm" style={{ display: "grid", gap: 4, color: "var(--oh-text)" }}>
      {libelle}
      <input data-testid={testId} value={valeur} placeholder={placeholder} autoComplete="off"
             onChange={(ev) => onChange(ev.target.value)} style={CHAMP} />
    </label>
  );
}

function Choix({ testId, titre, detail, onClick, actif }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick} aria-pressed={Boolean(actif)}
            style={{ textAlign: "left", borderRadius: 8, padding: 12, cursor: "pointer",
                     border: `0.5px solid ${actif ? "var(--oh-accent)" : "var(--oh-border)"}`,
                     background: actif ? "var(--oh-accent-bg)" : "var(--oh-bg)", color: "var(--oh-text)" }}>
      <div style={{ fontWeight: 500 }}>{titre}</div>
      {detail && <div className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>{detail}</div>}
    </button>
  );
}

function Accueil({ onNouvelle, onReprendre }) {
  const [enCours, setEnCours] = useState(null);
  const [erreur, setErreur] = useState("");
  useEffect(() => { mesVisitesEnCours().then(setEnCours).catch((err) => { setEnCours([]); setErreur(err.message); }); }, []);
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <Button data-testid="entree-nouvelle-visite" onClick={onNouvelle} style={PRIMAIRE}>Nouvelle visite</Button>
      {erreur && <Encart ton="error" testId="reprise-erreur">{erreur}</Encart>}
      {enCours === null && <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>Recherche des visites en cours…</p>}
      {enCours?.length > 0 && (
        <section data-testid="reprendre-visites" style={CARTE}>
          <div style={{ fontWeight: 500 }}>Reprendre une visite</div>
          {enCours.map((r) => (
            <Choix key={r.id} testId={`reprendre-${r.id}`} onClick={() => onReprendre(r)}
                   titre={r.contexte?.client || r.contexte?.dossier || "Visite en cours"}
                   detail={[r.contexte?.lieu, r.ouvert_le && `ouverte le ${new Date(r.ouvert_le).toLocaleString("fr-FR")}`,
                            `${r.nombre_postes || 0} ligne(s)`].filter(Boolean).join(" · ")} />
          ))}
        </section>
      )}
    </div>
  );
}

function NouvelleVisite({ onAnnuler, onConfirme, profil, activite }) {
  const [saisie, setSaisie] = useState({ client: "", lieu: "" });
  const [clients, setClients] = useState(null);
  const [client, setClient] = useState(null);
  const [lieux, setLieux] = useState(null);
  const [lieu, setLieu] = useState(null);               // { ...site } | { nouveau: {...} }
  const [nouveauLieu, setNouveauLieu] = useState(null);  // formulaire, avant choix
  const [dossier, setDossier] = useState(undefined);     // undefined = pas choisi · null = nouveau
  const [erreur, setErreur] = useState("");
  const [occupe, setOccupe] = useState(false);

  const dicte = useCallback((t) => setSaisie(decouperDictee(t)), []);
  const { disponible: micro, ecoute, erreur: erreurMicro, demarrer } = useDictee(dicte);

  const garder = async (f) => { setErreur(""); setOccupe(true); try { await f(); } catch (err) { setErreur(err.message); } finally { setOccupe(false); } };
  const rechercher = () => garder(async () => {
    setClient(null); setLieux(null); setLieu(null); setDossier(undefined);
    setClients(await chercherClients(saisie.client));
  });
  const choisirClient = (c) => garder(async () => {
    setClient(c); setLieu(null); setDossier(undefined); setNouveauLieu(null);
    setLieux(await lieuxDuClient(c.id, saisie.lieu));
  });
  const choisirLieu = (s) => { setLieu(s); setNouveauLieu(null); setDossier(s.dossiers?.length ? undefined : null); };
  const valider = () => garder(async () => {
    const libLieu = lieu.nouveau ? adresseLieu(lieu.nouveau) : adresseLieu(lieu);
    const r = await confirmer({ client, lieu, dossier, activite, profilCode: profil,
                                libelles: { client: nomClient(client), lieu: libLieu } });
    onConfirme({ dossierId: r.dossierId, client: nomClient(client), lieu: libLieu, reference: r.reference });
  });

  const dossiersDuLieu = lieu && !lieu.nouveau ? (lieu.dossiers || []) : [];
  const pret = client && lieu && dossier !== undefined;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <section style={CARTE} data-testid="saisie-visite">
        <div style={{ fontWeight: 500 }}>Nouvelle visite</div>
        {micro && (
          <Button variant="outline" data-testid="dicter-client-lieu" onClick={demarrer} disabled={ecoute} style={SECONDAIRE}>
            {ecoute ? "J'écoute…" : "Dicter le client et l'adresse"}
          </Button>
        )}
        {erreurMicro && <Encart ton="error" testId="dictee-erreur">{erreurMicro}</Encart>}
        <Champ libelle="Client" testId="saisie-client" valeur={saisie.client} placeholder="ex. Syndic ABC"
               onChange={(v) => setSaisie((s) => ({ ...s, client: v }))} />
        <Champ libelle="Adresse ou lieu" testId="saisie-lieu" valeur={saisie.lieu} placeholder="ex. Résidence des Lilas, 12 rue X"
               onChange={(v) => setSaisie((s) => ({ ...s, lieu: v }))} />
        <Button data-testid="rechercher-client" onClick={rechercher} disabled={occupe || saisie.client.trim().length < 2}
                style={client ? SECONDAIRE : PRIMAIRE}>Rechercher</Button>
      </section>

      {clients && (
        <section style={CARTE} data-testid="clients-proposes">
          <div style={{ fontWeight: 500 }}>Client</div>
          {clients.length === 0 && (
            <Encart ton="info" testId="aucun-client">Aucun client ne correspond. Vérifiez la saisie, ou créez le client depuis OmniHub.</Encart>
          )}
          {clients.map((c) => (
            <Choix key={c.id} testId={`client-${c.id}`} actif={client?.id === c.id} onClick={() => choisirClient(c)}
                   titre={nomClient(c)} detail={[c.telephone, c.email].filter(Boolean).join(" · ")} />
          ))}
        </section>
      )}

      {client && lieux && (
        <section style={CARTE} data-testid="lieux-proposes">
          <div style={{ fontWeight: 500 }}>Lieu de la visite</div>
          {lieux.map((s) => (
            <Choix key={s.id} testId={`lieu-${s.id}`} actif={lieu?.id === s.id} onClick={() => choisirLieu(s)}
                   titre={adresseLieu(s)} detail={s.score > 0 ? "Correspond à l'adresse saisie" : null} />
          ))}
          {!nouveauLieu && !lieu?.nouveau && (
            <Button variant="outline" data-testid="nouveau-lieu" style={DISCRET}
                    onClick={() => { setLieu(null); setNouveauLieu({ adresse: saisie.lieu, code_postal: "", ville: "" }); }}>
              Nouveau lieu pour ce client
            </Button>
          )}
          {nouveauLieu && (
            <div data-testid="formulaire-nouveau-lieu" style={{ display: "grid", gap: 8 }}>
              <Champ libelle="Adresse" testId="nouveau-lieu-adresse" valeur={nouveauLieu.adresse}
                     onChange={(v) => setNouveauLieu((n) => ({ ...n, adresse: v }))} />
              <Champ libelle="Code postal" testId="nouveau-lieu-cp" valeur={nouveauLieu.code_postal}
                     onChange={(v) => setNouveauLieu((n) => ({ ...n, code_postal: v }))} />
              <Champ libelle="Ville" testId="nouveau-lieu-ville" valeur={nouveauLieu.ville}
                     onChange={(v) => setNouveauLieu((n) => ({ ...n, ville: v }))} />
              <Button variant="outline" data-testid="nouveau-lieu-retenir" style={SECONDAIRE}
                      disabled={!nouveauLieu.adresse.trim() || !nouveauLieu.ville.trim()}
                      onClick={() => {
                        const n = { adresse: nouveauLieu.adresse.trim(), ville: nouveauLieu.ville.trim(),
                                    ...(nouveauLieu.code_postal.trim() ? { code_postal: nouveauLieu.code_postal.trim() } : {}) };
                        setLieu({ nouveau: n }); setNouveauLieu(null); setDossier(null);
                      }}>
                Retenir ce lieu (créé à la confirmation)
              </Button>
            </div>
          )}
          {lieu?.nouveau && (
            <Encart ton="info" testId="lieu-a-creer">Nouveau lieu, créé à la confirmation : {adresseLieu(lieu.nouveau)}</Encart>
          )}
        </section>
      )}

      {lieu && dossiersDuLieu.length > 0 && (
        <section style={CARTE} data-testid="dossiers-proposes">
          <div style={{ fontWeight: 500 }}>Dossier</div>
          {dossiersDuLieu.map((d) => (
            <Choix key={d.id} testId={`dossier-${d.id}`} actif={dossier?.id === d.id} onClick={() => setDossier(d)}
                   titre={d.reference || "Dossier"} detail={d.statut || null} />
          ))}
          <Choix testId="dossier-nouveau" actif={dossier === null} onClick={() => setDossier(null)}
                 titre="Nouveau dossier sur ce lieu" />
        </section>
      )}

      {pret && (
        <section style={CARTE} data-testid="confirmation-visite">
          <div style={{ fontWeight: 500 }}>À confirmer</div>
          <div className="text-sm" data-testid="recap-client">Client : {nomClient(client)}</div>
          <div className="text-sm" data-testid="recap-lieu">
            Lieu : {lieu.nouveau ? `${adresseLieu(lieu.nouveau)} (nouveau)` : adresseLieu(lieu)}
          </div>
          <div className="text-sm" data-testid="recap-dossier">
            Dossier : {dossier ? (dossier.reference || "existant") : "nouveau dossier"}
          </div>
          <Button data-testid="confirmer-visite" onClick={valider} disabled={occupe} style={PRIMAIRE}>
            {occupe ? "Ouverture…" : "Confirmer et ouvrir le relevé"}
          </Button>
        </section>
      )}

      {erreur && <Encart ton="error" testId="entree-erreur">{erreur}</Encart>}
      <Button variant="outline" data-testid="annuler-nouvelle-visite" onClick={onAnnuler} style={DISCRET}>Annuler</Button>
    </div>
  );
}

function EnteteVisite({ visite, onQuitter }) {
  return (
    <section data-testid="dossier-identifie" style={{ ...CARTE, gap: 4 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <strong data-testid="visite-client" style={{ fontWeight: 500 }}>{visite.client || visite.reference || "Visite"}</strong>
        <Button variant="outline" data-testid="quitter-visite" onClick={onQuitter} style={{ ...DISCRET, height: 32 }}>Accueil</Button>
      </div>
      {visite.lieu && <div className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>{visite.lieu}</div>}
      {visite.reference && <div className="text-sm" data-testid="dossier-reference" style={{ color: "var(--oh-text-muted)" }}>{visite.reference}</div>}
    </section>
  );
}

export function VisiteRenovation() {
  useManifest();
  // 🔴 P-MARIO-20 — la SESSION vient de l'AUTHENTIFICATION OMNIHUB (AuthProvider), ⛔ d'un
  //    jeton lu en local. Le spinner ne couvre que le PREMIER contrôle : pendant la
  //    soumission, le formulaire reste monté (leçon N°498 de MobileVisitePage).
  const { isAuthenticated, loading: authEnCours } = useAuth();
  const [authVerifiee, setAuthVerifiee] = useState(false);
  useEffect(() => { if (!authEnCours) setAuthVerifiee(true); }, [authEnCours]);
  const session = Boolean(isAuthenticated);
  const { profil, activite, pret } = useCapacites(session);
  const [etape, setEtape] = useState("accueil");
  const [visite, setVisite] = useState(null);

  let contenu;
  if (!authVerifiee) {
    contenu = <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>Chargement…</p>;
  } else if (!session) {
    // 🔴 P-MARIO-20 — ⛔ plus de lien vers « / » : sur visite.omnihub-ia.fr, « / » est
    //    redirigé par nginx vers /mobile/chantier (ancien parcours SMS). On se connecte
    //    ICI, avec l'écran de connexion terrain EXISTANT (même autorité que l'ERP :
    //    `useAuth().loginWithEmail`) ; la session se pose sur CETTE origine et l'écran
    //    reste sur /mobile/renovation — ⛔ aucune redirection, donc aucun retour à perdre.
    contenu = <ConnexionTerrain titre="Visite Rénovation" />;
  } else if (!pret) {
    contenu = <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>Chargement…</p>;
  } else if (!profil || !activite) {
    contenu = (
      <Encart ton="info" testId="profil-absent">
        Aucun profil de relevé n'est déclaré pour cette entreprise : la visite ne peut pas être ouverte.
      </Encart>
    );
  } else if (etape === "releve" && visite) {
    contenu = (
      <>
        <EnteteVisite visite={visite} onQuitter={() => { setVisite(null); setEtape("accueil"); }} />
        {/* 🔑 P-MARIO-15 — LE MÊME composant, avec les MÊMES props que la fiche dossier d'OmniHub
            (EmplacementVisites) : ⛔ un second relevé. */}
        <ReleveChantier dossierId={visite.dossierId} profilCode={profil}
                        actionsReleve={(r) => <CreerDevisReleve releve={r} onOuvrir={ouvrirPdfDevis} />} />
      </>
    );
  } else if (etape === "nouvelle") {
    contenu = (
      <NouvelleVisite profil={profil} activite={activite} onAnnuler={() => setEtape("accueil")}
                      onConfirme={(v) => { setVisite(v); setEtape("releve"); }} />
    );
  } else {
    contenu = (
      <Accueil onNouvelle={() => setEtape("nouvelle")}
               onReprendre={(r) => { setVisite({ dossierId: r.dossier_id, client: r.contexte?.client,
                                                 lieu: r.contexte?.lieu, reference: r.contexte?.dossier });
                                     setEtape("releve"); }} />
    );
  }

  return (
    <main data-testid="visite-renovation"
          style={{ minHeight: "100vh", background: "var(--oh-bg)", color: "var(--oh-text)", padding: 16 }}>
      <div style={{ maxWidth: 560, margin: "0 auto", display: "grid", gap: 16 }}>
        <h1 data-testid="visite-renovation-titre" style={{ fontSize: 20, fontWeight: 500, margin: 0 }}>{NOM}</h1>
        {contenu}
      </div>
    </main>
  );
}

export default VisiteRenovation;
